// WHAT: Trend analysis tab component displaying metric trends over different time periods
// WHY:  Provides comprehensive trend analysis for all Withings metrics with period-based evaluation
// DOMAIN: Health → Withings → Trend Analysis
// DEPENDENCIES: TrendAnalysisService, TrendCard component, WithingsMeasurement types
// RISKS:
//   - NOTE: Trend calculations depend on measurement data quality - may be inaccurate with sparse data
//   - NOTE: Timeframe changes trigger recalculation - may be slow with large datasets
//   - WARN: Trend evaluation thresholds are hardcoded - may not be appropriate for all users

import type { WithingsMeasurement as DatabaseWithingsMeasurement } from "../../types/database";

import React, { useState, useMemo } from "react";

import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { ToggleGroup } from "@/components/ui/ToggleGroup";

import { TrendAnalysisService } from "../../services/trendAnalysisService";
import { type WithingsMeasurement } from "../../types";
import { TrendCard } from "../molecules/TrendCard";
import { WithingsLoader } from "../ui";

interface TrendsTabProperties {
  isLoading?: boolean;
  measurements: WithingsMeasurement[];
}

/**
 * Gets unit string for a metric key
 *
 * @param metricKey - Metric key identifier
 *
 * @returns {string} Unit string or empty string if not found
 *
 */
const getUnit = (metricKey: string): string => {
  const config = TrendAnalysisService.getMetricConfig(metricKey);
  return config?.unit ?? "";
};

/**
 * WHAT:
 *   Trend analysis tab component displaying metric trends over different time periods.
 *
 * WHY:
 *   Provides comprehensive trend analysis for all Withings metrics with period-based evaluation.
 *
 * PROPS:
 *   - measurements: WithingsMeasurement[] — Array of measurements to analyze
 *   - isLoading?: boolean — Whether measurements are currently being loaded
 *
 * BEHAVIOR:
 *   - Allows timeframe selection (7 days, 30 days, 1 year)
 *   - Calculates trends for all metrics using TrendAnalysisService
 *   - Displays trend cards with direction indicators (up/down/stable)
 *   - Shows loading state while calculations are in progress
 *
 * RISKS:
 *   - NOTE: Trend calculations depend on measurement data quality - may be inaccurate with sparse data
 *   - NOTE: Timeframe changes trigger recalculation - may be slow with large datasets
 *   - WARN: Trend evaluation thresholds are hardcoded - may not be appropriate for all users
 *
 * @param root0
 *
 * @param root0.measurements
 *
 * @param root0.isLoading
 *
 * @returns {JSX.Element} Trends tab component with metric trend analysis
 *
 */
export const TrendsTab: React.FC<TrendsTabProperties> = ({ isLoading = false, measurements }) => {
  const [timeframe, setTimeframe] = useState<"1y" | "7d" | "30d">("7d");

  // NOTE: Calculate all trends for the current time period
  // PERF: useMemo prevents recalculation on every render - only recalculates when measurements or timeframe change
  const trends = useMemo(() => {
    return TrendAnalysisService.calculateAllTrends(
      measurements as DatabaseWithingsMeasurement[],
      timeframe,
    );
  }, [measurements, timeframe]);

  // Show loading state if no measurements and still loading
  if (isLoading && measurements.length === 0) {
    return <WithingsLoader />;
  }

  // Get current value for each metric
  const getCurrentValue = (metricKey: string): null | number => {
    if (measurements.length === 0) {
      return null;
    }
    const latest = measurements[0];
    if (!latest) {
      return null;
    }
    const config = TrendAnalysisService.getMetricConfig(metricKey);
    if (!config) {
      return null;
    }
    const metricValue = (latest as DatabaseWithingsMeasurement)[config.metric];
    return typeof metricValue === "number" ? metricValue : null;
  };

  const timeframeOptions = [
    { label: "7 Days", value: "7d" },
    { label: "30 Days", value: "30d" },
    { label: "1 Year", value: "1y" },
  ];

  return (
    <div className="space-y-6">
      {/* Time Period Filter */}
      <div className="flex justify-center">
        <ToggleGroup
          onChange={(value) => {
            setTimeframe(value as "1y" | "7d" | "30d");
          }}
          options={timeframeOptions}
          orientation="horizontal"
          size="md"
          value={timeframe}
          variant="default"
        />
      </div>

      {/* Trend Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <TrendCard
          currentValue={getCurrentValue("weight")}
          title="Weight"
          trend={trends.weight ?? null}
          unit={getUnit("weight")}
        />

        <TrendCard
          currentValue={getCurrentValue("bodyFat")}
          title="Body Fat"
          trend={trends.bodyFat ?? null}
          unit={getUnit("bodyFat")}
        />

        <TrendCard
          currentValue={getCurrentValue("muscleMass")}
          title="Muscle Mass"
          trend={trends.muscleMass ?? null}
          unit={getUnit("muscleMass")}
        />

        <TrendCard
          currentValue={getCurrentValue("fatMass")}
          title="Fat Mass"
          trend={trends.fatMass ?? null}
          unit={getUnit("fatMass")}
        />

        <TrendCard
          currentValue={getCurrentValue("bodyWater")}
          title="Body Water"
          trend={trends.bodyWater ?? null}
          unit={getUnit("bodyWater")}
        />

        <TrendCard
          currentValue={getCurrentValue("boneMass")}
          title="Bone Mass"
          trend={trends.boneMass ?? null}
          unit={getUnit("boneMass")}
        />

        <TrendCard
          currentValue={getCurrentValue("visceralFat")}
          title="Visceral Fat"
          trend={trends.visceralFat ?? null}
          unit={getUnit("visceralFat")}
        />

        <TrendCard
          currentValue={getCurrentValue("fatFreeMass")}
          title="Fat Free Mass (kg)"
          trend={trends.fatFreeMass ?? null}
          unit={getUnit("fatFreeMass")}
        />

        <TrendCard
          currentValue={getCurrentValue("fatFreeMassPercent")}
          title="Fat Free Mass (%)"
          trend={trends.fatFreeMassPercent ?? null}
          unit={getUnit("fatFreeMassPercent")}
        />

        <TrendCard
          currentValue={getCurrentValue("bmi")}
          title="BMI"
          trend={trends.bmi ?? null}
          unit={getUnit("bmi")}
        />
      </div>

      {/* Info Box */}
      <Card className="bg-slate-800/30 border-slate-600 text-white">
        <div className="p-4">
          <h3 className="text-lg font-semibold text-white mb-2">Trend Interpretation</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <Badge className="w-3 h-3 p-0 rounded-full" variant="success">
                {""}
              </Badge>
              <span className="text-green-300">Green: Improvement</span>
            </div>
            <div className="flex items-center space-x-2">
              <Badge className="w-3 h-3 p-0 rounded-full" variant="destructive">
                {""}
              </Badge>
              <span className="text-red-300">Red: Decline</span>
            </div>
            <div className="flex items-center space-x-2">
              <Badge className="w-3 h-3 p-0 rounded-full" variant="warning">
                {""}
              </Badge>
              <span className="text-yellow-300">Yellow: Stable</span>
            </div>
          </div>
          <p className="text-slate-400 text-sm mt-3">
            Trends are based on comparison between current and oldest values in the selected time
            period. Small changes are classified as &quot;stable&quot;.
          </p>
        </div>
      </Card>
    </div>
  );
};
